import { useEffect, useRef, useState, FormEvent } from "react";
import { AlertFrequency, ALERT_FREQUENCIES } from "../../services/api";
import styles from "./SaveSearchModal.module.scss";

type Props = {
  open: boolean;
  saving: boolean;
  error: string;
  onSave: (title: string, frequency: AlertFrequency) => void;
  onClose: () => void;
};

/**
 * "Save Search" dialog for the React map: a name for the search and how often
 * to email when new listings or price changes match. Styled like ListModal.
 * The frequency values are the codes SavedSearch.UpdateFrequency stores
 * (SavedSearchFrequency on the server), which double as the labels.
 */
export default function SaveSearchModal({ open, saving, error, onSave, onClose }: Props) {
  const [title, setTitle] = useState("");
  const [frequency, setFrequency] = useState<AlertFrequency>("Daily");
  const nameRef = useRef<HTMLInputElement>(null);

  // Fresh form each time it opens; focus the name, and hand focus back on close.
  useEffect(() => {
    if (!open) return;
    const opener = document.activeElement as HTMLElement | null;
    setTitle("");
    setFrequency("Daily");
    const focusTimer = window.setTimeout(() => nameRef.current?.focus(), 0);
    return () => {
      window.clearTimeout(focusTimer);
      opener?.focus?.();
    };
  }, [open]);

  // Close on Escape while open.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !saving) onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, saving, onClose]);

  if (!open) return null;

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const name = title.trim();
    if (!name || saving) return;
    onSave(name, frequency);
  };

  return (
    <div
      className={styles.overlay}
      onClick={(e) => {
        if (e.target === e.currentTarget && !saving) onClose();
      }}
    >
      <div
        className={styles.dialog}
        role="dialog"
        aria-modal="true"
        aria-labelledby="save-search-title"
        aria-describedby="save-search-sub"
      >
        <button
          type="button"
          className={styles.close}
          aria-label="Close"
          onClick={onClose}
          disabled={saving}
        >
          &times;
        </button>
        <h2 id="save-search-title" className={styles.title}>
          Save this search
        </h2>
        <p id="save-search-sub" className={styles.sub}>
          Find it again any time on your account page.
        </p>

        <form className={styles.form} onSubmit={submit}>
          <label className={styles.label} htmlFor="save-search-name">
            Search name
          </label>
          <input
            id="save-search-name"
            ref={nameRef}
            className={styles.input}
            type="text"
            maxLength={255}
            required
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />

          <label className={styles.label} htmlFor="save-search-frequency">
            Email me when new listings or price changes match this search
          </label>
          <select
            id="save-search-frequency"
            className={styles.input}
            value={frequency}
            onChange={(e) => setFrequency(e.target.value as AlertFrequency)}
          >
            {ALERT_FREQUENCIES.map((f) => (
              <option key={f} value={f}>
                {f}
              </option>
            ))}
          </select>

          <div className={styles.actions}>
            <button type="button" className={styles.secondary} onClick={onClose} disabled={saving}>
              Cancel
            </button>
            <button type="submit" className={styles.primary} disabled={saving || !title.trim()}>
              {saving ? "Saving…" : "Save Search"}
            </button>
          </div>
        </form>

        {error && (
          <p className={styles.error} role="alert">
            {error}
          </p>
        )}
      </div>
    </div>
  );
}
